import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Menú de filtros del mapa táctico.
 *
 * ── Por qué existe ──────────────────────────────────────────────────────────
 * Los filtros de amenaza vivían en una fila de botones dentro del encabezado de
 * la sección: una tira que crecía con cada tipo de alerta del catálogo, empujaba
 * el mapa hacia abajo y obligaba a hacer scroll para ver la operación completa.
 * Con veinte tipos de amenaza ocupaba dos renglones y el mapa quedaba en la
 * mitad inferior de la pantalla.
 *
 * Aquí los filtros son un botón en el propio mapa. El espacio que ocupan es
 * constante —un botón— sin importar cuántas opciones haya, y el buscador hace
 * que veinte opciones sean tan usables como tres.
 *
 * ── Grupos, no una lista plana ──────────────────────────────────────────────
 * «Amenazas» es de selección única porque así funciona el filtro de casos: se
 * mira una amenaza a la vez. «Estado» y «Capas» son de selección múltiple. El
 * modo lo declara cada grupo en vez de estar cableado acá, que es lo que permite
 * agregar un grupo nuevo sin tocar este componente.
 */

export type MapFilterMode = "single" | "multi";

export interface MapFilterOption {
  id: string;
  label: string;
  /** Casos abiertos, unidades en ese estado… Se pinta a la derecha. */
  count?: number;
  active: boolean;
  /** Color del punto de la opción. Se usa para los estados de vigilancia. */
  tone?: string;
}

export interface MapFilterGroup {
  id: string;
  label: string;
  mode: MapFilterMode;
  options: MapFilterOption[];
  onToggle: (optionId: string) => void;
}

export interface MapFilterMenuProps {
  groups: MapFilterGroup[];
  /** Cuántos filtros están activos. Se pinta en el botón. */
  activeCount: number;
  onClear: () => void;
}

/**
 * Normaliza para buscar: sin acentos y sin mayúsculas.
 *
 * Sin esto, escribir «jamming» encuentra la amenaza pero «desviacion» no
 * encuentra «Desviación de ruta», que es justo como la va a escribir un
 * monitorista con prisa.
 */
const normalizar = (valor: string) =>
  valor
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export default function MapFilterMenu({ groups, activeCount, onClear }: MapFilterMenuProps) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Cerrar al hacer clic fuera o con Escape. Sin esto el panel se queda abierto
  // encima del mapa y tapa justo la zona que el monitorista quiere mirar.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setQuery("");
  }, [open]);

  const visibles = useMemo(() => {
    const q = normalizar(query.trim());
    if (!q) return groups;

    return groups
      .map((group) => ({
        ...group,
        options: group.options.filter((option) => normalizar(option.label).includes(q)),
      }))
      .filter((group) => group.options.length > 0);
  }, [groups, query]);

  const totalOpciones = groups.reduce((sum, group) => sum + group.options.length, 0);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={t("monitoreoIa.monitoringCenter.filters.open")}
        className={`flex h-8 items-center gap-1.5 rounded-lg border px-2.5 max-sm:h-10 text-[11px] font-medium transition-colors ${
          activeCount > 0
            ? "border-primary-500 bg-primary-500 text-on-primary"
            : "border-line bg-surface/90 text-content-secondary hover:border-primary-500/40 hover:text-content"
        }`}
      >
        <i className="ri-equalizer-line text-[13px]" />
        <span>{t("monitoreoIa.monitoringCenter.filters.label")}</span>
        {activeCount > 0 ? (
          <span className="rounded bg-on-primary/20 px-1 font-mono text-[10px] font-bold">
            {activeCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label={t("monitoreoIa.monitoringCenter.filters.label")}
          className="absolute left-0 top-9 z-[6] w-72 max-w-[calc(100vw-1rem)] overflow-hidden max-sm:top-11 rounded-xl border border-line bg-surface shadow-xl"
        >
          <div className="border-b border-line p-2">
            <div className="relative">
              <i className="ri-search-line pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[13px] text-content-faint" />
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t("monitoreoIa.monitoringCenter.filters.search")}
                className="h-8 w-full rounded-lg border border-line bg-subtle pl-7 pr-2 text-[11px] text-content outline-none placeholder:text-content-faint focus:border-primary-500"
              />
            </div>
          </div>

          <div className="max-h-[320px] overflow-y-auto">
            {visibles.length === 0 ? (
              <p className="px-3 py-6 text-center text-[11px] text-content-faint">
                {t("monitoreoIa.monitoringCenter.filters.noMatches")}
              </p>
            ) : (
              visibles.map((group) => (
                <div key={group.id} className="border-b border-line last:border-b-0">
                  <p className="px-3 pb-1 pt-2 font-mono text-[9.5px] uppercase tracking-widest text-content-muted">
                    {group.label}
                  </p>

                  {group.options.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => group.onToggle(option.id)}
                      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[11.5px] transition-colors hover:bg-subtle ${
                        option.active ? "text-content" : "text-content-secondary"
                      }`}
                    >
                      <span
                        className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center border transition-colors ${
                          group.mode === "single" ? "rounded-full" : "rounded-[3px]"
                        } ${
                          option.active
                            ? "border-primary-500 bg-primary-500 text-on-primary"
                            : "border-line-soft"
                        }`}
                      >
                        {option.active ? (
                          <i
                            className={`${
                              group.mode === "single" ? "ri-checkbox-blank-circle-fill text-[7px]" : "ri-check-line text-[10px]"
                            }`}
                          />
                        ) : null}
                      </span>

                      {option.tone ? (
                        <span
                          className="h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{ background: option.tone }}
                        />
                      ) : null}

                      <span className="min-w-0 flex-1 truncate">{option.label}</span>

                      {option.count != null ? (
                        <span
                          className={`shrink-0 font-mono text-[10px] ${
                            option.count > 0 ? "text-content-secondary" : "text-content-faint"
                          }`}
                        >
                          {option.count}
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-line px-3 py-2">
            <span className="font-mono text-[9.5px] uppercase tracking-widest text-content-faint">
              {t("monitoreoIa.monitoringCenter.filters.total", { count: totalOpciones })}
            </span>
            <button
              type="button"
              onClick={onClear}
              disabled={activeCount === 0}
              className="text-[11px] font-medium text-primary-500 transition-opacity hover:underline disabled:pointer-events-none disabled:opacity-40"
            >
              {t("monitoreoIa.monitoringCenter.filters.clear")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
